// Sequential PCM16 audio player for realtime voice output.
//
// Audio arrives as a stream of base64 PCM16 chunks (24 kHz mono). Each chunk
// is decoded into an AudioBuffer and scheduled back-to-back on a single
// AudioContext timeline so playback is gapless. `onStateChange(speaking)`
// fires when the queue transitions between playing and idle.

import { SAMPLE_RATE, base64ToInt16, int16ToFloat32 } from './pcm.js';

export class VoicePlayer {
  constructor(onStateChange) {
    this.ctx = null;
    this.nextStartTime = 0;
    this.active = new Set();
    this.speaking = false;
    this.onStateChange = onStateChange || (() => {});
    // Analyser tapped by the UI to pulse the voice orb with the assistant's
    // output amplitude. Sits between every source and the destination.
    this.analyser = null;
    this._levelBuf = null;
  }

  ensureContext() {
    if (!this.ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      this.ctx = new Ctx({ sampleRate: SAMPLE_RATE });
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.connect(this.ctx.destination);
      this._levelBuf = new Uint8Array(this.analyser.fftSize);
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
    return this.ctx;
  }

  /** Current output amplitude (RMS, 0..1) for the orb. 0 when idle. */
  getLevel() {
    if (!this.analyser || !this.speaking) return 0;
    this.analyser.getByteTimeDomainData(this._levelBuf);
    let sum = 0;
    for (let i = 0; i < this._levelBuf.length; i += 1) {
      const v = (this._levelBuf[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / this._levelBuf.length);
  }

  setSpeaking(value) {
    if (this.speaking === value) return;
    this.speaking = value;
    this.onStateChange(value);
  }

  enqueue(base64Pcm) {
    const ctx = this.ensureContext();
    const float = int16ToFloat32(base64ToInt16(base64Pcm));
    if (!float.length) return;

    const buffer = ctx.createBuffer(1, float.length, SAMPLE_RATE);
    buffer.copyToChannel(float, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.analyser || ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now, this.nextStartTime);
    source.start(startAt);
    this.nextStartTime = startAt + buffer.duration;

    this.active.add(source);
    this.setSpeaking(true);
    source.onended = () => {
      this.active.delete(source);
      if (this.active.size === 0) this.setSpeaking(false);
    };
  }

  /** Stop everything immediately (barge-in / interruption). */
  clear() {
    for (const source of this.active) {
      try { source.stop(); } catch (_e) { /* already stopped */ }
    }
    this.active.clear();
    this.nextStartTime = this.ctx ? this.ctx.currentTime : 0;
    this.setSpeaking(false);
  }

  async close() {
    this.clear();
    if (this.ctx) {
      try { await this.ctx.close(); } catch (_e) { /* ignore */ }
      this.ctx = null;
    }
    this.analyser = null;
    this._levelBuf = null;
  }
}
